import React, { useState, useRef, useEffect } from 'react';
import {
  Button, Box, TextField,
  Typography, ToggleButtonGroup, ToggleButton,
  Switch, FormControlLabel, Alert, LinearProgress, Stack, Chip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LockIcon from '@mui/icons-material/Lock';
import {
  uploadGalleryImage, GalleryImage, GalleryQuota,
} from '../../services/gallery_api';
import { GALLERY_UPLOAD_INPUT_LIMIT, GALLERY_CAPTION_MAX_LENGTH } from '../../utils/subscription';
import GameSelectDialog, { GameSelectResult } from '../dialogs/GameSelectDialog';
import BaseDialog from '../dialogs/BaseDialog';

interface Props {
  open: boolean;
  onClose: () => void;
  onUploaded: (image: GalleryImage) => void;
  quota: GalleryQuota | null;
}

// 画質ティア（2560px は撤廃）。locked=プラン外はグレー表示＋PRO誘導。
const QUALITY_TIERS = [
  { px: 1280, label: '標準', sub: '1280px' },
  { px: 1920, label: '高画質', sub: '1920px' },
];

const RESOLUTION_TO_TIER: Record<number, number> = { 1280: 1, 1920: 2 };

const GalleryUploadDialog: React.FC<Props> = ({ open, onClose, onUploaded, quota }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [game, setGame] = useState<GameSelectResult | null>(null);
  const [gameSelectOpen, setGameSelectOpen] = useState(false);
  const [resolution, setResolution] = useState<number>(1280);
  const [isSpoiler, setIsSpoiler] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // プランで選べる画質。有料は高画質(1920)を既定にして恩恵を強調。
  const available = quota?.resolution_options || [1280];
  const initialized = useRef(false);
  useEffect(() => {
    if (open && !initialized.current && quota) {
      setResolution(Math.max(...(quota.resolution_options || [1280])));
      initialized.current = true;
    }
    if (!open) initialized.current = false;
  }, [open, quota]);

  const resetForm = () => {
    setFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setCaption('');
    setGame(null);
    setResolution(1280);
    setIsSpoiler(false);
    setError(null);
    setProgress(0);
  };

  const handleClose = () => {
    if (uploading) return;
    resetForm();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > GALLERY_UPLOAD_INPUT_LIMIT) {
      setError(`ファイルサイズは${GALLERY_UPLOAD_INPUT_LIMIT / 1024 / 1024}MB以下にしてください。`);
      return;
    }
    setError(null);
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setError(null);
    try {
      // POST完了で即時クローズ。画像処理(Celery)は親側のplaceholder+自動ポーリングが拾う。
      const created = await uploadGalleryImage(
        {
          file,
          caption: caption.trim() || undefined,
          game_id: game?.game_id,
          resolution_tier: RESOLUTION_TO_TIER[resolution] || 1,
          visibility: 0,   // 公開範囲機能は現状撤去（全画像=公開）
          is_spoiler: isSpoiler,
        },
        (p) => setProgress(p),
      );
      onUploaded(created);
      resetForm();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'アップロードに失敗しました。');
    } finally {
      setUploading(false);
    }
  };

  const quotaReached = quota && (
    (quota.count_limit >= 0 && quota.count_used >= quota.count_limit) ||
    quota.storage_used_bytes >= quota.storage_limit_bytes
  );

  return (
    <>
      <BaseDialog
        open={open}
        onClose={handleClose}
        title="画像をアップロード"
        maxWidth="sm"
        actions={
          <>
            <Button onClick={handleClose} disabled={uploading}>キャンセル</Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!file || uploading || !!quotaReached}
            >
              アップロード
            </Button>
          </>
        }
      >
        {quotaReached && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            容量または枚数の上限に達しています。
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!file ? (
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: '2px dashed', borderColor: 'divider', borderRadius: 2,
              p: 4, textAlign: 'center', cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
            }}
          >
            <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              クリックして画像を選択（JPEG / PNG / WebP / GIF）
            </Typography>
            <Typography variant="caption" color="text.secondary">
              最大 {GALLERY_UPLOAD_INPUT_LIMIT / 1024 / 1024}MB
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </Box>
        ) : (
          <Stack spacing={2}>
            {previewUrl && (
              <Box sx={{ width: '100%', maxHeight: 300, overflow: 'hidden', borderRadius: 1, backgroundColor: '#000' }}>
                <img src={previewUrl} alt="preview" style={{ width: '100%', maxHeight: 300, objectFit: 'contain' }} />
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {file.name} ({(file.size / 1024 / 1024).toFixed(2)}MB)
              </Typography>
              <Button size="small" onClick={() => { setFile(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }}>
                変更
              </Button>
            </Box>

            <TextField
              label="コメント (任意)"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              multiline
              rows={2}
              inputProps={{ maxLength: GALLERY_CAPTION_MAX_LENGTH }}
              helperText={`${caption.length} / ${GALLERY_CAPTION_MAX_LENGTH}`}
              fullWidth
            />

            <Box>
              <Typography variant="caption" color="text.secondary">紐付けゲーム (任意)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                {game ? (
                  <Chip
                    label={game.japanese_name || game.name}
                    onDelete={() => setGame(null)}
                    sx={{ maxWidth: '100%' }}
                  />
                ) : (
                  <Button size="small" variant="outlined" onClick={() => setGameSelectOpen(true)}>
                    ゲームを選択
                  </Button>
                )}
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                画質
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={resolution}
                onChange={(_, v) => {
                  if (v == null) return;
                  if (!available.includes(v)) {
                    // ロック（プラン外）→ 課金導線
                    window.open('/pricing', '_blank', 'noopener,noreferrer');
                    return;
                  }
                  setResolution(v);
                }}
              >
                {QUALITY_TIERS.map((t) => {
                  const locked = !available.includes(t.px);
                  return (
                    <ToggleButton
                      key={t.px}
                      value={t.px}
                      sx={{
                        flexDirection: 'column', gap: 0.25, py: 0.75,
                        ...(locked && { color: 'text.disabled' }),
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {locked && <LockIcon sx={{ fontSize: 12 }} />}
                        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem' }}>
                          {t.label}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', opacity: 0.85 }}>
                        {t.sub}{locked ? ' · PRO' : ''}
                      </Typography>
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
              {available.length < QUALITY_TIERS.length && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5, fontSize: '0.62rem' }}>
                  高画質(1920px)は
                  <Box
                    component="span"
                    onClick={() => window.open('/pricing', '_blank', 'noopener,noreferrer')}
                    sx={{ color: 'primary.main', cursor: 'pointer', mx: 0.25, '&:hover': { textDecoration: 'underline' } }}
                  >
                    PROプラン
                  </Box>
                  で解放されます
                </Typography>
              )}
            </Box>

            <FormControlLabel
              control={<Switch checked={isSpoiler} onChange={(e) => setIsSpoiler(e.target.checked)} />}
              label="ネタバレを含む"
            />

            {uploading && (
              <Box>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  アップロード中 {progress}%
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </BaseDialog>

      <GameSelectDialog
        open={gameSelectOpen}
        onClose={() => setGameSelectOpen(false)}
        onSelect={(g) => { setGame(g); setGameSelectOpen(false); }}
        title="紐付けゲームを選択"
      />
    </>
  );
};

export default GalleryUploadDialog;
